import AuthGuard from "@auth/auth.guard";
import AuthContext from "@auth/context/Auth.context";

import { useConfigurationContext } from "@hooks/index";
import Keycloak from "keycloak-js";
import { useCallback, useEffect, useState } from "react";

const TOKEN_MIN_VALIDITY = 5 * 60; // 5 minutos

import {
  getStorageItem,
  removeStorageItem,
  STORAGE_KEY_FILTERS,
  setStorageItem,
} from "@shared/utils/localStorage";

enum ROLES {
  admin,
  "read-only",
  "timeline-manager",
}

export type RolesType = keyof typeof ROLES;

let keycloak: Keycloak.KeycloakInstance;

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState("");
  const [idToken, setIdToken] = useState("");
  const [userId, setUserId] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [errorKeycloak, setErrorKeycloak] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState("Sentinel");

  const config = useConfigurationContext();

  const updateToken = useCallback(() => {
    if (keycloak.token) {
      setToken(keycloak.token);
      if (keycloak.idToken) {
        setIdToken(keycloak.idToken);
      }

      const subject = keycloak.tokenParsed.sub;
      if (typeof subject === "string") {
        setUserId(subject);
      }

      const userName = keycloak.tokenParsed.displayName;
      if (userName?.trim()) {
        setUserDisplayName(userName);
      }
    }
  }, []);

  const onReady = useCallback(() => {
    setInitialized(true);
  }, []);

  const onAuthError = useCallback(() => {
    setErrorKeycloak(true);
    removeStorageItem(STORAGE_KEY_FILTERS);
  }, []);

  const onAuthSuccess = useCallback(() => {
    setAuthenticated(true);
    updateToken();
  }, [updateToken]);

  const onTokenExpired = useCallback(() => {
    if (keycloak) {
      keycloak.updateToken(TOKEN_MIN_VALIDITY);
    }
  }, []);

  const onAuthRefreshSuccess = useCallback(() => {
    updateToken();
  }, [updateToken]);

  useEffect(() => {
    if (config) {
      const realm = getStorageItem("realm", config.SENTINEL_KEYCLOAK_REALM);
      const options = {
        url: config.SENTINEL_KEYCLOAK_URL,
        clientId: config.SENTINEL_KEYCLOAK_CLIENT_ID,
        realm,
      };

      keycloak = new Keycloak(options);

      keycloak.init({
        onLoad: "check-sso",
        checkLoginIframe: false,
      });

      keycloak.onReady = onReady;
      keycloak.onAuthError = onAuthError;
      keycloak.onAuthSuccess = onAuthSuccess;
      keycloak.onTokenExpired = onTokenExpired;
      keycloak.onAuthRefreshSuccess = onAuthRefreshSuccess;
    }
  }, [
    config,
    onReady,
    onAuthError,
    onAuthSuccess,
    onTokenExpired,
    onAuthRefreshSuccess,
  ]);

  const hasSomeResourceRole = (...roles: RolesType[]): boolean => {
    return roles
      .map((role) =>
        keycloak.hasResourceRole(role, "ambevtech-logistica-sentinel")
      )
      .some(Boolean);
  };

  const login = (realm: "Ambev" | "Third") => {
    keycloak = new Keycloak({
      url: config.SENTINEL_KEYCLOAK_URL,
      clientId: config.SENTINEL_KEYCLOAK_CLIENT_ID,
      realm,
    });

    keycloak.init({
      onLoad: "check-sso",
      checkLoginIframe: true,
    });

    keycloak.onReady = onReady;
    keycloak.onAuthError = onAuthError;
    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onTokenExpired = onTokenExpired;
    keycloak.onAuthRefreshSuccess = onAuthRefreshSuccess;

    try {
      keycloak.login({
        redirectUri: window.location.origin,
        prompt: "login",
      });

      setStorageItem("realm", realm);
    } catch {
      setErrorKeycloak(true);
    }
  };

  const logout = () => {
    removeStorageItem(STORAGE_KEY_FILTERS);
    removeStorageItem("realm");

    keycloak.logout();
    setAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        idToken,
        userId,
        login,
        logout,
        userDisplayName,
        initialized,
        authenticated,
        errorKeycloak,
        hasSomeResourceRole,
      }}
    >
      <AuthGuard>{children}</AuthGuard>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
